'use client';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { RefreshCw, ShieldCheck } from 'lucide-react';
import { forwardRef, useEffect, useImperativeHandle, useState } from 'react';

const HUMAN_VERIFICATION_STORAGE_KEY = 'warrantyPilotHumanVerified';

interface CaptchaChallenge {
  left: number;
  right: number;
  answer: number;
}

export interface HumanVerificationChallengeHandle {
  verify: () => boolean;
}

interface HumanVerificationChallengeProps {
  className?: string;
}

export const HumanVerificationChallenge = forwardRef<HumanVerificationChallengeHandle, HumanVerificationChallengeProps>(
  function HumanVerificationChallenge({ className = '' }, ref) {
    const [captcha, setCaptcha] = useState<CaptchaChallenge | null>(null);
    const [captchaAnswer, setCaptchaAnswer] = useState('');
    const [error, setError] = useState('');
    const [isVerified, setIsVerified] = useState(false);

    useEffect(() => {
      if (hasStoredHumanVerificationProof()) {
        setIsVerified(true);
        return;
      }

      setCaptcha(createCaptchaChallenge());
    }, []);

    const refreshCaptcha = () => {
      setCaptcha(createCaptchaChallenge());
      setCaptchaAnswer('');
      setError('');
    };

    const verify = () => {
      if (isVerified || hasStoredHumanVerificationProof()) {
        setIsVerified(true);
        return true;
      }

      if (!captcha) {
        setError('Verification is still loading. Please try again.');
        return false;
      }

      if (Number(captchaAnswer) === captcha.answer) {
        storeHumanVerificationProof();
        setIsVerified(true);
        setError('');
        return true;
      }

      setError('Please solve the quick verification question');
      return false;
    };

    useImperativeHandle(ref, () => ({ verify }), [captcha?.answer, captchaAnswer, isVerified]);

    if (isVerified) {
      return null;
    }

    return (
      <div className={`space-y-2 rounded-md border border-gray-200 bg-gray-50 p-3 text-left ${className}`}>
        <div className="flex items-center justify-between gap-3">
          <Label htmlFor="human-verification" className="flex items-center gap-2 text-sm font-medium leading-6">
            <ShieldCheck className="h-4 w-4 text-[#00B67A]" />
            {captcha ? `Verify you're human: ${captcha.left} + ${captcha.right} =` : 'Preparing verification...'}
          </Label>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={refreshCaptcha}
            disabled={!captcha}
            className="h-8 shrink-0 px-2 text-xs text-[#00A067] hover:bg-green-50 hover:text-[#008F5C]"
          >
            <RefreshCw className="mr-1 h-3 w-3" />
            New
          </Button>
        </div>
        <Input
          id="human-verification"
          inputMode="numeric"
          value={captchaAnswer}
          onChange={(event) => {
            setCaptchaAnswer(event.target.value.replace(/\D/g, ''));
            if (error) {
              setError('');
            }
          }}
          placeholder="Answer"
          disabled={!captcha}
          className="bg-white"
        />
        {error ? <p className="text-sm text-red-600">{error}</p> : null}
      </div>
    );
  },
);

function hasStoredHumanVerificationProof() {
  try {
    return typeof localStorage !== 'undefined' && localStorage.getItem(HUMAN_VERIFICATION_STORAGE_KEY) === 'true';
  } catch {
    return false;
  }
}

function storeHumanVerificationProof() {
  try {
    if (typeof localStorage !== 'undefined') {
      localStorage.setItem(HUMAN_VERIFICATION_STORAGE_KEY, 'true');
    }
  } catch {
    // If storage is unavailable, the in-memory verified state still avoids asking again in this session.
  }
}

function createCaptchaChallenge(): CaptchaChallenge {
  const left = randomChallengeNumber();
  const right = randomChallengeNumber();
  return {
    left,
    right,
    answer: left + right,
  };
}

function randomChallengeNumber() {
  return Math.floor(Math.random() * 8) + 2;
}
